import React, { useState } from 'react';
import ApiClient, { Form, Channel, getDefaultApiHostname, normalizeApiHostname } from '../api/client';
import ConvertKitFieldMappingEditor from './ConvertKitFieldMappingEditor';
import MauticFieldMappingEditor from './MauticFieldMappingEditor';
import ResponseConfigEditor from './ResponseConfigEditor';
import ChannelSelector from './ChannelSelector';
import { ModalShell } from './ui';
import './FormDetails.css';

interface FormDetailsProps {
  form: Form;
  apiClient: ApiClient;
  onClose: () => void;
  onFormUpdated: (form: Form) => void;
  onEditForm?: (form: Form) => void;
}

const FormDetails: React.FC<FormDetailsProps> = ({
  form,
  apiClient,
  onClose,
  onFormUpdated,
  onEditForm,
}) => {
  const [channels, setChannels] = useState<Channel[]>(form.channels || []);
  const [flaggedChannels, setFlaggedChannels] = useState<Channel[]>(form.flaggedChannels || []);
  const [spamChannels, setSpamChannels] = useState<Channel[]>(form.spamChannels || []);
  const [availableChannels, setAvailableChannels] = useState<Channel[]>([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showChannelSelector, setShowChannelSelector] = useState(false);
  const [showFlaggedChannelSelector, setShowFlaggedChannelSelector] = useState(false);
  const [showSpamChannelSelector, setShowSpamChannelSelector] = useState(false);
  const [copiedItem, setCopiedItem] = useState<string | null>(null);

  React.useEffect(() => {
    loadAvailableChannels();
  }, []);

  const loadAvailableChannels = async () => {
    setLoadingChannels(true);
    try {
      const allChannels = await apiClient.listChannels();
      const attachedIds = new Set(channels.map((c) => c.id));
      setAvailableChannels(allChannels.filter((c) => !attachedIds.has(c.id)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load channels');
    } finally {
      setLoadingChannels(false);
    }
  };

  const handleAttachChannel = async (channelId: number) => {
    try {
      const result = await apiClient.attachChannelToForm(form.id, channelId);
      setChannels(result.channels);
      await loadAvailableChannels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to attach channel');
    }
  };

  const handleDetachChannel = async (channelId: number) => {
    try {
      const result = await apiClient.detachChannelFromForm(form.id, channelId);
      setChannels(result.channels);
      await loadAvailableChannels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to detach channel');
    }
  };

  const handleCopyEndpoint = () => {
    const hostname = normalizeApiHostname(localStorage.getItem('apiHostname') || getDefaultApiHostname());
    const endpoint = `${hostname}/f/${form.hash}`;
    navigator.clipboard.writeText(endpoint);
    setCopiedItem('endpoint');
    setTimeout(() => setCopiedItem(null), 2000);
  };

  const handleCopyHash = () => {
    navigator.clipboard.writeText(form.hash);
    setCopiedItem('hash');
    setTimeout(() => setCopiedItem(null), 2000);
  };

  return (
    <>
      <ModalShell
        title={form.name}
        onClose={onClose}
        className="form-details-modal"
        headerActions={
          <>
          {onEditForm && (
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => onEditForm(form)}
              title="Edit form information"
            >
              Edit
            </button>
          )}
          </>
        }
        footer={
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        }
      >
      <div className="form-details-panel">
        {error && (
          <div className="alert alert-error">
            <span>Warning</span>
            <span>{error}</span>
          </div>
        )}

        {/* Basic Info Grid */}
        <div className="details-section form-details-grid">
          <div>
            <h4>Basic Information</h4>
            <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
              <label>Name:</label>
              <span style={{ textAlign: 'left', marginLeft: 0, marginTop: '4px' }}>{form.name}</span>
            </div>
            <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', marginTop: '12px' }}>
              <label>Domain:</label>
              <span style={{ textAlign: 'left', marginLeft: 0, marginTop: '4px' }}>{form.domain}</span>
            </div>
            {form.description && (
              <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', marginTop: '12px' }}>
                <label>Description:</label>
                <span style={{ textAlign: 'left', marginLeft: 0, marginTop: '4px' }}>{form.description}</span>
              </div>
            )}
            <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', marginTop: '12px' }}>
              <label>Created:</label>
              <span style={{ textAlign: 'left', marginLeft: 0, marginTop: '4px' }}>{new Date(form.createdAt).toLocaleDateString()}</span>
            </div>
            <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', marginTop: '12px' }}>
              <label>Spam Detection:</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                {(form as any).spamDetectionDisabled && (
                  <span className="status-badge status-spam">Disabled</span>
                )}
                {form.testMode && (
                  <span className="status-badge status-flagged">Test mode</span>
                )}
                {!(form as any).spamDetectionDisabled && (
                  <span style={{ fontSize: '13px', color: '#666' }}>
                    Threshold: {form.spamThreshold != null ? form.spamThreshold : '0.8 (default)'}
                  </span>
                )}
              </div>
            </div>
            <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', marginTop: '12px' }}>
              <label>Notifications:</label>
              <span className={`status-badge ${form.notificationsEnabled !== false ? 'status-approved' : 'status-spam'}`}>
                {form.notificationsEnabled !== false ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>

          <div>
            <h4>Endpoints & Access</h4>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px' }}>Form Hash</label>
              <div className="hash-display">
                <code>{form.hash}</code>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={handleCopyHash}
                  title="Copy hash"
                  data-copied={copiedItem === 'hash' ? 'true' : undefined}
                >
                  {copiedItem === 'hash' ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px' }}>Submission Endpoint</label>
              <div className="endpoint-display">
                <code>/f/{form.hash}</code>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={handleCopyEndpoint}
                  title="Copy endpoint"
                  data-copied={copiedItem === 'endpoint' ? 'true' : undefined}
                >
                  {copiedItem === 'endpoint' ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {form.allowedOrigins && form.allowedOrigins.length > 0 && (
          <div className="details-section">
            <h4>Allowed Origins</h4>
            <div className="origins-list">
              {form.allowedOrigins.map((origin, idx) => (
                <code key={idx}>{origin}</code>
              ))}
            </div>
          </div>
        )}

        {/* Channels Section - Approved Notifications */}
        <div className="details-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div>
              <h4 style={{ margin: 0 }}>Notification Channels ({channels.length})</h4>
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#666' }}>
                Receive notifications when submissions are approved
              </p>
            </div>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => setShowChannelSelector(true)}
            >
              Manage
            </button>
          </div>

          {channels.length === 0 ? (
            <div className="empty-state compact-empty">
              <p>No channels attached. Click "Manage" to add channels.</p>
            </div>
          ) : (
            <div className="channels-list">
              {channels.map((channel) => (
                <div key={channel.id} className="channel-item" style={{ padding: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: '500', fontSize: '13px', color: '#333', marginBottom: '4px' }}>{channel.name}</div>
                    <div style={{ fontSize: '11px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {channel.type}
                    </div>
                  </div>
                  <div style={{ fontSize: '13px', color: '#666' }}>
                    {channel.formCount || 0} form{channel.formCount !== 1 ? 's' : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Flagged Channels Section - Review Alerts */}
        <div className="details-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div>
              <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                Flagged Notification Channels ({flaggedChannels.length})
              </h4>
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#666' }}>
                Receive alerts when submissions need review (spam score 0.5-0.8)
              </p>
            </div>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => setShowFlaggedChannelSelector(true)}
            >
              Manage
            </button>
          </div>

          {flaggedChannels.length === 0 ? (
            <div className="empty-state compact-empty">
              <p>No flagged channels configured. Click "Manage" to add review alert channels.</p>
            </div>
          ) : (
            <div className="channels-list">
              {flaggedChannels.map((channel) => (
                <div key={channel.id} className="channel-item" style={{ padding: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderLeft: '3px solid #ffc107' }}>
                  <div>
                    <div style={{ fontWeight: '500', fontSize: '13px', color: '#333', marginBottom: '4px' }}>{channel.name}</div>
                    <div style={{ fontSize: '11px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {channel.type}
                    </div>
                  </div>
                  <div style={{ fontSize: '13px', color: '#666' }}>
                    {channel.formCount || 0} form{channel.formCount !== 1 ? 's' : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Spam Channels Section - Spam Webhooks */}
        <div className="details-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div>
              <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                Spam Notification Channels ({spamChannels.length})
              </h4>
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#666' }}>
                Trigger webhooks when submissions are marked as spam (auto or manual)
              </p>
            </div>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => setShowSpamChannelSelector(true)}
            >
              Manage
            </button>
          </div>

          {spamChannels.length === 0 ? (
            <div className="empty-state compact-empty">
              <p>No spam channels configured. Click "Manage" to add webhook channels for spam notifications.</p>
            </div>
          ) : (
            <div className="channels-list">
              {spamChannels.map((channel) => (
                <div key={channel.id} className="channel-item" style={{ padding: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderLeft: '3px solid #dc3545' }}>
                  <div>
                    <div style={{ fontWeight: '500', fontSize: '13px', color: '#333', marginBottom: '4px' }}>{channel.name}</div>
                    <div style={{ fontSize: '11px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {channel.type}
                    </div>
                  </div>
                  <div style={{ fontSize: '13px', color: '#666' }}>
                    {channel.formCount || 0} form{channel.formCount !== 1 ? 's' : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submission Response Config */}
        <ResponseConfigEditor
          formId={form.id}
          apiClient={apiClient}
          responseMode={(form as any).responseMode || 'json'}
          responseConfig={(form as any).responseConfig || null}
          onUpdated={() => onFormUpdated(form)}
        />

        {/* ConvertKit Field Mapping - Full Width */}
        <ConvertKitFieldMappingEditor
          formId={form.id}
          apiClient={apiClient}
          hasConvertkitChannels={channels.some((c) => c.type === 'convertkit')}
        />

        {/* Mautic Field Mapping - Full Width */}
        <MauticFieldMappingEditor
          formId={form.id}
          apiClient={apiClient}
          hasMauticChannels={channels.some((c) => c.type === 'mautic')}
          mauticChannelId={channels.find((c) => c.type === 'mautic')?.id}
        />
      </div>
      </ModalShell>

      {showChannelSelector && (
        <ChannelSelector
          formId={form.id}
          apiClient={apiClient}
          attachedChannels={channels}
          onChannelsChange={(updatedChannels) => {
            setChannels(updatedChannels);
            onFormUpdated({...form, channels: updatedChannels});
          }}
          onClose={() => setShowChannelSelector(false)}
        />
      )}

      {showFlaggedChannelSelector && (
        <ChannelSelector
          formId={form.id}
          apiClient={apiClient}
          attachedChannels={flaggedChannels}
          onChannelsChange={(updatedChannels) => {
            setFlaggedChannels(updatedChannels);
            onFormUpdated({...form, flaggedChannels: updatedChannels});
          }}
          onClose={() => setShowFlaggedChannelSelector(false)}
          mode="flagged"
        />
      )}

      {showSpamChannelSelector && (
        <ChannelSelector
          formId={form.id}
          apiClient={apiClient}
          attachedChannels={spamChannels}
          onChannelsChange={(updatedChannels) => {
            setSpamChannels(updatedChannels);
            onFormUpdated({...form, spamChannels: updatedChannels});
          }}
          onClose={() => setShowSpamChannelSelector(false)}
          mode="spam"
        />
      )}
    </>
  );
};

export default FormDetails;
